import React, { memo, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Marker, Polyline, InfoWindow } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['geometry'];

const defaultMapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  mapTypeControlOptions: { position: 3 },
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: 'greedy',
  styles: [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] }
  ]
};

/**
 * GoogleMapDisplay - A reusable Google Maps component for displaying zones, markers, and routes
 * 
 * Props:
 * - center: { lat, lng } - Map center
 * - zoom: number - Zoom level (default: 14)
 * - style: object - Container style
 * - polygon: [[lat, lng], ...] - Polygon boundary points
 * - polygonOptions: object - Polygon styling options
 * - markers: [{ lat, lng, label, icon, color, content }] - Marker data
 * - polylines: [{ path: [[lat, lng], ...], color, weight }] - Route polylines
 * - circle: { center: { lat, lng }, radius: number } - Circle overlay
 * - onClick: function - Map click handler
 */
const GoogleMapDisplay = ({
  center = { lat: 29.7604, lng: -95.3698 },
  zoom = 14,
  style = { height: '100%', width: '100%' },
  polygon = null,
  polygonOptions = {},
  markers = [],
  polylines = [],
  circle = null,
  onClick = null,
  children
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-loader',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  // Convert polygon from [[lat, lng], ...] to [{ lat, lng }, ...]
  const polygonPath = useMemo(() => {
    if (!polygon || polygon.length < 3) return null;
    return polygon.map(p => ({ lat: p[0], lng: p[1] }));
  }, [polygon]);

  // Default polygon options
  const defaultPolygonOptions = {
    fillColor: '#8b5cf6',
    fillOpacity: 0.2,
    strokeColor: '#8b5cf6',
    strokeWeight: 2,
    clickable: false,
    ...polygonOptions
  };

  if (!isLoaded) {
    return (
      <div style={style} className="flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={style}
      center={center}
      zoom={zoom}
      options={defaultMapOptions}
      onClick={onClick}
    >
      {/* Polygon overlay */}
      {polygonPath && (
        <Polygon
          paths={polygonPath}
          options={defaultPolygonOptions}
        />
      )}

      {/* Circle overlay */}
      {circle && circle.center && (
        <Marker
          position={circle.center}
          icon={{
            path: window.google?.maps?.SymbolPath?.CIRCLE,
            scale: 8,
            fillColor: '#8b5cf6',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2
          }}
        />
      )}

      {/* Markers */}
      {markers.map((marker, idx) => {
        if (!marker.lat || !marker.lng) return null;
        
        const position = { lat: marker.lat, lng: marker.lng };
        const color = marker.color || '#8b5cf6';
        
        return (
          <Marker
            key={marker.id || idx}
            position={position}
            title={marker.label || marker.address || ''}
            icon={marker.icon ? marker.icon : {
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                  <circle cx="16" cy="16" r="14" fill="${color}" stroke="white" stroke-width="2"/>
                  <text x="16" y="21" font-family="Arial" font-size="12" fill="white" text-anchor="middle">${marker.number || idx + 1}</text>
                </svg>
              `)}`,
              scaledSize: { width: 32, height: 32 },
              anchor: { x: 16, y: 16 }
            }}
            zIndex={100 + idx}
          />
        );
      })}

      {/* Polylines (routes) */}
      {polylines.map((line, idx) => {
        if (!line.path || line.path.length < 2) return null;
        
        const path = line.path.map(p => 
          Array.isArray(p) ? { lat: p[0], lng: p[1] } : p
        );
        
        return (
          <Polyline
            key={idx}
            path={path}
            options={{
              strokeColor: line.color || '#f97316',
              strokeWeight: line.weight || 3,
              strokeOpacity: line.opacity || 0.8
            }}
          />
        );
      })}

      {children}
    </GoogleMap>
  );
};

export default memo(GoogleMapDisplay);
